<template>
  <div class="modal-selectable-area">
    <div class="modal-selectable-wrapper">
      <h3 class="selectable-title">Ürün Varyantları</h3>
      <p class="selectable-text">
        {{
          hasClickedProductVariants
            ? "20 seçeneğe kadar ürün varyantı ekleyebilirsiniz."
            : "Bu üründe varyant seçeneği bulunmamaktadır."
        }}
      </p>
      <DropdownSelectable
        ref="variantsSelectableRef"
        id="variant-selectable"
        :class="{ disabled: !productVariants?.length }"
        :drop-items="productVariants || []"
        label="Varyant"
        :errorText="isShowVariantsErrorText"
        @selectedItems="handleSelectedVariantItems($event)"
        @changedDropdownState="handleChangedVariantsDropdownState($event)"
        :disabledCheckboxChecked="true"
        :disabledCheckboxText="'Bu ürün varyantı, listenizde mevcut.'"
      />
    </div>
    <div class="modal-selectable-wrapper">
      <h3 class="selectable-title">Teslimat Şablonu</h3>
      <p class="selectable-text">
        Ürünün teslimat seçeneğini belirlemek için şablon seçin.
      </p>
      <DropdownSelectable
        ref="shipmentTemplateSelectableRef"
        id="shipment-template-selectable"
        :dropItems="shipmentTemplatesTransform || []"
        label="Teslimat Şablonu"
        isRadioGroup
        :errorText="isShowTemplatesErrorText"
        contentBtnText="Yeni Şablon Oluştur"
        :contentBtnClick="navigateCreateNewTemplate"
        :dropdownStateProps="dropdownState"
        @selectedRadioItem="handleSelectedRadioItem($event)"
        @changedDropdownState="handleChangedTemplatesDropdownState($event)"
      >
        <template #contentBtnIcon>
          <Icons name="Plus" color="#5D3EBC" />
        </template>
      </DropdownSelectable>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { DropdownSelectable, Icons } from "@n11/so-ui-components";
import { useStore } from "vuex";

const emit = defineEmits(["selectedShipmentTemplate", "selectedVariants"]);
const props = defineProps({
  isClickOpenSaleProductBtn: {
    type: Boolean,
    required: false,
    default: false,
  },
  clickedProductModalData: {
    type: [Object, Boolean],
    required: false,
    default: false,
  },
  hasClickedProductVariants: {
    type: Boolean,
    required: false,
    default: true,
  },
});

const store = useStore();

const shipmentTemplates = ref();
const variantsSelectableRef = ref();
const shipmentTemplateSelectableRef = ref();
const isClickCreateTemplateBtnClick = ref(false);
const dropdownState = ref(false);
const selectedShipmentTemplate = ref({});
const selectedVariants = ref([]);
const isFirstOpenTemplateSelectable = ref(false);
const isFirstOpenVariantsSelectable = ref(false);

const getCatalogModalDeleteRowId = computed(
  () => store.getters["navigation/getCatalogModalDeleteRowId"]
);

const productVariants = computed(() => {
  return props.clickedProductModalData?.variantList?.map((item) => ({
    id: item.id,
    name: item.value,
    gtin: props.clickedProductModalData?.productList.find(
      (i) => i.pimsId === item.id
    )?.gtin,
    stockCode: null,
    listPrice: null,
    salePrice: null,
    stock: null,
    duration: null,
    imgUrl: props.clickedProductModalData?.productList.find(
      (i) => i.pimsId === item.id
    )?.imageUrl,
    disabled: props.clickedProductModalData?.productList.find(
      (i) => i.pimsId === item.id
    )?.isSkuExist,
    validations: {
      stockCode: {
        isDirty: false,
        error: false,
        message: null,
      },
      listPrice: {
        isDirty: false,
        error: false,
        message: null,
      },
      salePrice: {
        isDirty: false,
        error: false,
        message: null,
      },
      stock: {
        isDirty: false,
        error: false,
        message: null,
      },
      duration: {
        isDirty: false,
        error: false,
        message: null,
      },
      vatRate: {
        isDirty: false,
        error: false,
        message: null,
      },
    },
  }));
});

const shipmentTemplatesTransform = computed(() => {
  return shipmentTemplates.value?.map((item) => ({
    id: item.id,
    name: item.templateName,
  }));
});

const isShowTemplatesErrorText = computed(() => {
  if (
    Object.keys(selectedShipmentTemplate.value ?? {}).length === 0 &&
    (isFirstOpenTemplateSelectable.value || props.isClickOpenSaleProductBtn)
  ) {
    return "Teslimat şablonu ekleyin.";
  } else {
    return "";
  }
});

const isShowVariantsErrorText = computed(() => {
  if (
    props.hasClickedProductVariants &&
    selectedVariants.value.length === 0 &&
    (isFirstOpenVariantsSelectable.value || props.isClickOpenSaleProductBtn)
  ) {
    return "Lütfen varyant seçin.";
  } else {
    return "";
  }
});

const handleChangedTemplatesDropdownState = (event) => {
  dropdownState.value = event;

  if (event) {
    isFirstOpenTemplateSelectable.value = true;
  }

  if (isClickCreateTemplateBtnClick.value === true) {
    shipmentTemplates.value = [];
    emit("selectedShipmentTemplate", {});
    shipmentTemplateSelectableRef.value.selectedRadioItem = null;
    shipmentTemplateSelectableRef.value.getRadioButtonGroupRef().selectedItem =
      {};
    isClickCreateTemplateBtnClick.value = false;
  }
};

const handleChangedVariantsDropdownState = (event) => {
  if (event) {
    isFirstOpenVariantsSelectable.value = true;
  }
};

const getShipmentTemplates = async () => {
  try {
    const res = await store.dispatch("templates/getShipmentTemplates");
    shipmentTemplates.value = res.data.shipmentTemplates;
  } catch (error) {
    console.error("Failed to fetch shipment templates:", error);
  }
};

getShipmentTemplates();

watch(isClickCreateTemplateBtnClick, (newVal) => {
  if (!newVal) {
    getShipmentTemplates();
  }
});

watch(getCatalogModalDeleteRowId, (newVal) => {
  if (newVal) {
    variantsSelectableRef.value.selectedItems =
      variantsSelectableRef.value.selectedItems.filter(
        (item) => item.id !== newVal
      );
    store.commit("navigation/SET_CATALOG_MODAL_DELETE_ROW_ID", null);
  }
});

const navigateCreateNewTemplate = () => {
  isClickCreateTemplateBtnClick.value = true;
  dropdownState.value = false;
  window.open("/selleroffice/product/shippingInformationList", "_blank");
};

const handleSelectedRadioItem = (event) => {
  emit("selectedShipmentTemplate", event);
  selectedShipmentTemplate.value = event;

  if (event) {
    dropdownState.value = false;
  }
};

const handleSelectedVariantItems = (event) => {
  if (event.length > 20) {
    store.commit("common/SET_TOASTER_DATA", {
      type: "error",
      title: "",
      description: "Maximum 20 adet varyant seçilebilir.",
    });
    variantsSelectableRef.value.selectedItems.pop();
  } else {
    const deepClonedEvent = JSON.parse(JSON.stringify(event));
    emit("selectedVariants", deepClonedEvent);
    selectedVariants.value = deepClonedEvent;
  }
};

const addSelectedItemsValue = (pimsId) => {
  variantsSelectableRef.value.selectedItems.push(
    variantsSelectableRef.value.data.find((item) => item.id === pimsId)
  );
};

defineExpose({
  addSelectedItemsValue,
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.modal-selectable-area {
  display: flex;
  gap: 24px;
  margin-bottom: 36px;

  .modal-selectable-wrapper {
    max-width: 360px;
    width: 100%;
    position: relative;
    z-index: 2;

    :deep(#variant-selectable.disabled) {
      cursor: default;
      pointer-events: none;

      .dropdownSelectable-btn {
        background-color: #f6f7f9;
      }
    }

    &:first-child {
      padding-right: 24px;
      border-right: 1px solid $colorD20;
    }

    .selectable-title {
      font-size: 16px;
      line-height: 20px;
      font-weight: 700;
      letter-spacing: -0.05px;
      margin-bottom: 2px;
    }

    .selectable-text {
      font-size: 13px;
      line-height: 18px;
      margin-bottom: 8px;
    }
  }
}
</style>
